import { useCallback } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { handleError, triggerDownload } from 'utils/download';
import { hasValue } from 'utils/hasValue';

import { getCollectionOrderReportsUuidDownload } from 'api';
import type { OrderReports } from 'api';

import { useOrganisationId } from 'hooks/context';

import { TableLayout } from 'components/ui/templates/TableLayout/TableLayout';

import { DownloadPayinReportsDialog } from './DownloadPayinReportsDialog';
import { ExportedPayinsHeader } from './ExportedPayinsHeader';
import { ExportedPayinsTable } from './ExportedPayinsTable';
import type { FiltersState } from './localState';
import { useLocalState } from './localState';
import { payinReportFilename } from './reportFilename';

export const ExportedPayins = () => {
  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();

  const {
    state: localState,
    dispatch,
    resetState,
    setFilters: updateFilters,
    updateCursorAfter
  } = useLocalState();

  const setFilters = (value: FiltersState) => {
    dispatch(updateFilters(value));
  };

  const downloadReport = useCallback(
    async (report: OrderReports) => {
      const uuid = report.uuid;
      if (!hasValue(uuid)) return;

      try {
        const data = await getCollectionOrderReportsUuidDownload(
          organisationId,
          uuid
        );
        triggerDownload(
          new Blob([data], { type: 'text/csv' }),
          payinReportFilename(report.created_after, report.created_before)
        );
      } catch {
        handleError(openNotification, 'Error while downloading the file');
      }
    },
    [organisationId, openNotification]
  );

  return (
    <>
      <TableLayout
        header={
          <ExportedPayinsHeader
            filters={localState.filters}
            setFilters={setFilters}
            resetState={resetState}
          />
        }
        content={
          <ExportedPayinsTable
            localState={localState}
            updateCursorAfter={updateCursorAfter}
            downloadReport={downloadReport}
          />
        }
      />
      <DownloadPayinReportsDialog downloadReport={downloadReport} />
    </>
  );
};
